import { type ReactNode, useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from './auth/useAuth'
import { useIdleTimeout } from './auth/useIdleTimeout'
import { MeProvider } from './auth/MeContext'
import Layout from './components/Layout'
import LoadingSpinner from './components/LoadingSpinner'
import IdleWarningModal from './components/IdleWarningModal'
import { homeForRole, SOLO_TEST } from './nav'

// Pantallas del rediseño
import Inbox from './pages/Inbox'
import MyPerformance from './pages/MyPerformance'
import Templates from './pages/Templates'
import Queue from './pages/Queue'
import LiveMonitor from './pages/LiveMonitor'
import Advisors from './pages/Advisors'

// Pantallas existentes. Conservan su ruta: la reagrupación del sidebar las
// convirtió en sub-pestañas, no las movió de lugar.
import Panel from './pages/Panel'
import Conversations from './pages/Conversations'
import ConversationDetail from './pages/ConversationDetail'
import Expedientes from './pages/Expedientes'
import MisExpedientes from './pages/MisExpedientes'
import ReglasExpedientes from './pages/ReglasExpedientes'
import Candidates from './pages/Candidates'
import TestData from './pages/TestData'
import LeadsSocio from './pages/LeadsSocio'
import ReiniciarPruebas from './pages/ReiniciarPruebas'
import DocValidation from './pages/DocValidation'
import AgentAccess from './pages/AgentAccess'
import Documents from './pages/Documents'
import Outreach from './pages/Outreach'
import Agents from './pages/Agents'
import Speech from './pages/Speech'
import Knowledge from './pages/Knowledge'
import Integrations from './pages/Integrations'
import GoogleCallback from './pages/GoogleCallback'
import WhatsApp from './pages/WhatsApp'
import Users from './pages/Users'
import Config from './pages/Config'
import Audit from './pages/Audit'
import Incidents from './pages/Incidents'
import Traces from './pages/Traces'
import Logs from './pages/Logs'
import Metrics from './pages/Metrics'
import Costs from './pages/Costs'

// ── Google "G" logo SVG ───────────────────────────────────────────────────────

function GoogleLogo() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  )
}

// ── Pantalla de login ─────────────────────────────────────────────────────────

/** Fondo del login: el mismo verde del sidebar, para que el portal se
 *  reconozca antes de entrar. */
function PantallaOscura({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-sidebar font-sans">
      {children}
    </div>
  )
}

function LoginScreen() {
  const { login, isLoading } = useAuth()

  if (isLoading) {
    return (
      <PantallaOscura>
        <LoadingSpinner size="lg" label="Verificando sesión..." />
      </PantallaOscura>
    )
  }

  return (
    <PantallaOscura>
      <div className="flex w-[380px] flex-col items-center rounded-card bg-white px-8 pb-8 pt-[34px] shadow-login">
        <img src="/logo-universal.png" alt="COE"
             className="h-[46px] w-[46px] object-contain" />
        <h1 className="mt-3 text-[19px] font-extrabold text-ink">Portal PGH</h1>
        <p className="mt-1 text-body text-ink-muted">Telemarketing · COE Automatización IA</p>

        <div className="mb-[18px] mt-5 h-px w-full bg-surface-border" />

        <button
          onClick={login}
          className="flex w-full items-center justify-center gap-[11px] rounded-input border border-surface-border bg-white py-3 text-card font-bold text-ink transition-colors hover:bg-surface"
        >
          <GoogleLogo />
          Continuar con Google
        </button>

        <p className="mt-4 text-center text-micro text-ink-faint">
          Solo cuentas <b className="text-ink-muted">@prestamype.com</b>.
          El acceso se valida contra Cognito.
        </p>
      </div>
    </PantallaOscura>
  )
}

// ── PKCE callback handler ─────────────────────────────────────────────────────

/**
 * Motivos de rechazo que devuelve Cognito en `error_description`, traducidos.
 * Los escribe `lambdas/pre_sign_up/handler.py`: si cambia el texto allá, acá
 * cae al mensaje genérico, que igual muestra el original.
 */
const RECHAZOS: Array<[string, string]> = [
  ['has not been granted portal access',
   'Tu cuenta no tiene acceso a este portal todavía. Un Admin tiene que invitarte desde '
   + 'Administración › Usuarios; en un ambiente nuevo, el primer Admin se crea fuera del portal.'],
  ['corporate Google account required',
   'Solo se puede entrar con una cuenta de Google de prestamype.com.'],
]

function AuthCallback() {
  const { isAuthenticated, isLoading, login } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true })
  }, [isAuthenticated, navigate])

  // CUANDO COGNITO RECHAZA, LA PANTALLA LO DICE
  //
  // Cognito vuelve acá con `?error=…&error_description=…` y sin `code`. Hasta
  // el 28-set-2026 esta pantalla mostraba el spinner igual, para siempre: en
  // PROD, con el pool vacío, dos personas quedaron mirando «Verificando
  // sesión…» sin saber que el problema era que nadie las había invitado.
  const error = params.get('error')
  const detalle = params.get('error_description') ?? ''
  // Sin error y sin canje en curso, tampoco hay nada que esperar: pasa si el
  // canje del código falló o si alguien entró a esta dirección a mano.
  const sinNadaQueEsperar = !isLoading && !isAuthenticated
  if (error || sinNadaQueEsperar) {
    const traducido = RECHAZOS.find(([clave]) => detalle.includes(clave))?.[1]
    return (
      <PantallaOscura>
        <div className="flex w-[440px] flex-col items-center rounded-card bg-white p-10 text-center shadow-login">
          <span className="text-4xl" aria-hidden>🔒</span>
          <h1 className="mt-3 text-[19px] font-extrabold text-ink">No se pudo iniciar sesión</h1>
          <p className="mt-2 text-chat leading-relaxed text-ink-muted">
            {traducido ?? (detalle || 'La sesión no se pudo verificar. Vuelve a intentarlo.')}
          </p>
          {traducido && detalle && (
            <p className="mt-3 font-mono text-meta text-ink-faint">{detalle}</p>
          )}
          <button onClick={login} className="btn-primary mt-6">Volver a intentar</button>
        </div>
      </PantallaOscura>
    )
  }

  return (
    <PantallaOscura>
      <LoadingSpinner size="lg" label="Verificando sesión..." />
    </PantallaOscura>
  )
}

// ── Logeado pero sin rol ──────────────────────────────────────────────────────

function NoAccessScreen() {
  const { user, logout } = useAuth()
  return (
    <PantallaOscura>
      <div className="flex w-[440px] flex-col items-center rounded-card bg-white p-10 text-center shadow-login">
        <span className="text-4xl" aria-hidden>🔒</span>
        <h1 className="mt-3 text-[19px] font-extrabold text-ink">Acceso pendiente</h1>
        <p className="mt-2 text-chat leading-relaxed text-ink-muted">
          Iniciaste sesión como <b className="text-ink">{user?.email}</b>, pero tu cuenta todavía
          no tiene un rol asignado en el Portal PGH.
        </p>
        <p className="mt-3 text-body leading-relaxed text-ink-muted">
          Pedile a un administrador que te agregue en <b>Administración › Usuarios</b> como
          Asesor, Supervisor o Admin. Si acabas de recibir el rol, cierra sesión y vuelve a entrar:
          el rol viaja en el token y el actual se emitió antes del cambio.
        </p>
        <button onClick={logout} className="btn-primary mt-6">Cerrar sesión</button>
      </div>
    </PantallaOscura>
  )
}

function SinPermiso() {
  const { role } = useAuth()
  const navigate = useNavigate()
  // Mensaje explícito en vez de redirigir en silencio: quien llega acá pegó un
  // link que le compartieron, y un rebote sin explicación parece que el portal
  // se rompió.
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
      <p className="text-page font-extrabold text-ink">Esta pantalla no es de tu rol</p>
      <p className="max-w-md text-body text-ink-muted">
        Tu rol es <b>{role}</b> y esta sección pertenece a otro. Si necesitas acceso, pídeselo a
        un administrador.
      </p>
      <button className="btn-ghost mt-1" onClick={() => navigate(homeForRole(role), { replace: true })}>
        Volver a mi pantalla
      </button>
    </div>
  )
}

/**
 * Guard de ruta por rol.
 *
 * Es la SEGUNDA capa: la primera es que el ítem no aparece en el sidebar, y la
 * tercera —la que de verdad protege los datos— es el RBAC de `portal_api`.
 * Esta existe para que un link pegado a mano no muestre una pantalla llena de
 * 403 en vez de un mensaje claro.
 */
function Solo({ roles, children }: { roles: Array<'asesor' | 'supervisor' | 'admin'>; children: ReactNode }) {
  const { role } = useAuth()
  if (!role || !roles.includes(role)) return <SinPermiso />
  return <>{children}</>
}

const SUPERVISION = ['supervisor', 'admin'] as const
const SOLO_ADMIN = ['admin'] as const
const TODOS = ['asesor', 'supervisor', 'admin'] as const

// ── Auth gate ─────────────────────────────────────────────────────────────────

function AuthGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, hasNoRole, logout } = useAuth()
  const authed = isAuthenticated && !isLoading && !hasNoRole
  const { warningVisible, secondsRemaining, stayLoggedIn } = useIdleTimeout({
    onTimeout: logout,
    enabled: authed,
  })

  if (!isAuthenticated || isLoading) return <LoginScreen />
  if (hasNoRole) return <NoAccessScreen />
  return (
    <>
      {children}
      {warningVisible && (
        <IdleWarningModal
          secondsRemaining={secondsRemaining}
          onStay={stayLoggedIn}
          onLogout={logout}
        />
      )}
    </>
  )
}

/**
 * "/" es el Panel de supervisión y de Admin: lo que necesita una persona
 * ahora. El asesor no tiene panel — su trabajo ya está ordenado en la bandeja,
 * y ahí cae al entrar.
 */
function Home() {
  const { role } = useAuth()
  if (role === 'admin' || role === 'supervisor') return <Panel />
  return <Navigate to={homeForRole(role)} replace />
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="auth/callback" element={<AuthCallback />} />

        <Route
          path="*"
          element={
            <AuthGate>
              <MeProvider>
                <Routes>
                  <Route element={<Layout />}>
                    <Route index element={<Home />} />

                    {/* ── Asesor ─────────────────────────────────────────── */}
                    <Route path="inbox" element={<Solo roles={[...TODOS]}><Inbox /></Solo>} />
                    <Route path="mis-expedientes" element={<Solo roles={[...TODOS]}><MisExpedientes /></Solo>} />
                    <Route path="my-performance" element={<Solo roles={[...TODOS]}><MyPerformance /></Solo>} />
                    <Route path="templates" element={<Solo roles={[...TODOS]}><Templates /></Solo>} />

                    {/* ── Operación (supervisión) ────────────────────────── */}
                    <Route path="queue" element={<Solo roles={[...SUPERVISION]}><Queue /></Solo>} />
                    <Route path="live" element={<Solo roles={[...SUPERVISION]}><LiveMonitor /></Solo>} />
                    <Route path="advisors" element={<Solo roles={[...SUPERVISION]}><Advisors /></Solo>} />
                    <Route path="conversations" element={<Solo roles={[...SUPERVISION]}><Conversations /></Solo>} />
                    <Route path="conversations/:id" element={<Solo roles={[...SUPERVISION]}><ConversationDetail /></Solo>} />
                    <Route path="expedientes" element={<Solo roles={[...SUPERVISION]}><Expedientes /></Solo>} />

                    {/* ── Documentos ────────────────────────────────────── */}
                    <Route path="documents" element={<Solo roles={[...SUPERVISION]}><Documents /></Solo>} />
                    <Route path="doc-validation" element={<Solo roles={[...SUPERVISION]}><DocValidation /></Solo>} />

                    {/* ── Análisis ──────────────────────────────────────── */}
                    <Route path="metrics" element={<Solo roles={[...SUPERVISION]}><Metrics /></Solo>} />

                    {/* ── Configuración (admin) ─────────────────────────── */}
                    <Route path="speech" element={<Solo roles={[...SOLO_ADMIN]}><Speech /></Solo>} />
                    <Route path="knowledge" element={<Solo roles={[...SOLO_ADMIN]}><Knowledge /></Solo>} />
                    <Route path="agents" element={<Solo roles={[...SOLO_ADMIN]}><Agents /></Solo>} />
                    <Route path="whatsapp" element={<Solo roles={[...SOLO_ADMIN]}><WhatsApp /></Solo>} />
                    <Route path="integrations" element={<Solo roles={[...SOLO_ADMIN]}><Integrations /></Solo>} />
                    {/* A donde vuelve Google al conectar la cuenta que archiva en Drive. La
                        dirección está registrada en el cliente OAuth: cambiarla acá sin
                        cambiarla allá rompe la conexión. */}
                    <Route path="integraciones/google" element={<Solo roles={[...SOLO_ADMIN]}><GoogleCallback /></Solo>} />
                    <Route path="candidates" element={<Solo roles={[...SOLO_ADMIN]}><Candidates /></Solo>} />
                    <Route path="agent-access" element={<Solo roles={[...SOLO_ADMIN]}><AgentAccess /></Solo>} />
                    <Route path="outreach" element={<Solo roles={[...SOLO_ADMIN]}><Outreach /></Solo>} />
                    <Route path="config" element={<Solo roles={[...SOLO_ADMIN]}><Config /></Solo>} />
                    <Route path="reglas-expedientes" element={<Solo roles={[...SOLO_ADMIN]}><ReglasExpedientes /></Solo>} />
                    <Route path="users" element={<Solo roles={[...SOLO_ADMIN]}><Users /></Solo>} />
                    <Route path="costs" element={<Solo roles={[...SOLO_ADMIN]}><Costs /></Solo>} />
                    <Route path="test-data" element={<Solo roles={[...SOLO_ADMIN]}><TestData /></Solo>} />
                    {SOLO_TEST && (
                      <>
                        <Route path="test-leads" element={<Solo roles={[...SOLO_ADMIN]}><LeadsSocio /></Solo>} />
                        <Route path="test-reset" element={<Solo roles={[...SOLO_ADMIN]}><ReiniciarPruebas /></Solo>} />
                      </>
                    )}
                    <Route path="audit" element={<Solo roles={[...SOLO_ADMIN]}><Audit /></Solo>} />

                    {/* Observabilidad. Incidencias y trazas son de supervisión:
                        un supervisor necesita saber por qué un cliente no
                        recibió su documento. Los logs son solo de Admin porque
                        traen teléfonos y contenido de mensajes sin el filtro
                        por asignación que sí tienen las conversaciones. */}
                    <Route path="incidents" element={<Solo roles={[...SUPERVISION]}><Incidents /></Solo>} />
                    <Route path="traces" element={<Solo roles={[...SUPERVISION]}><Traces /></Solo>} />
                    <Route path="logs" element={<Solo roles={[...SOLO_ADMIN]}><Logs /></Solo>} />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
              </MeProvider>
            </AuthGate>
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
